// Sample domain API client — maps the Sampler service calls onto /api/v1/samples.
// Every endpoint returns the envelope {code, data, message}; we unwrap `data`.

import { SAMPLES_BASE, type Brief, type Sample, type SampleActivity, type SampleComment, type SampleReference } from './lib'

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${SAMPLES_BASE}${path}`, {
    ...init,
    headers: { 'content-type': 'application/json', ...(init?.headers || {}) },
  })
  const body = (await res.json().catch(() => ({}))) as {
    data?: T
    message?: string
    detail?: { message?: string } | string
  }
  if (!res.ok) {
    const d = body.detail
    const msg =
      typeof d === 'string' ? d : (d?.message ?? body.message ?? `HTTP ${res.status}`)
    throw new Error(msg)
  }
  return body.data as T
}

export type SampleListResponse = { items: Sample[]; trash_count: number }

export const listSamples = () => call<SampleListResponse>('')
export const listTrash = () => call<{ items: Sample[] }>('/trash')
export const getSample = (id: string) => call<Sample>(`/${id}`)
export const createSample = (data: { title: string; description?: string; etsy_url?: string; preview?: unknown }) =>
  call<Sample>('', { method: 'POST', body: JSON.stringify(data) })
export const duplicateSample = (id: string) =>
  call<Sample>(`/${id}/duplicate`, { method: 'POST' })
export const deleteSample = (id: string) => call<unknown>(`/${id}`, { method: 'DELETE' })
export const restoreSample = (id: string) => call<unknown>(`/${id}/restore`, { method: 'POST' })
export const purgeSample = (id: string) => call<unknown>(`/${id}/purge`, { method: 'DELETE' })

export const setPriority = (id: string, priority: string) =>
  call<Sample>(`/${id}`, { method: 'PATCH', body: JSON.stringify({ priority }) })

export const urlPreview = (url: string) =>
  call<Record<string, unknown>>('/url-preview', { method: 'POST', body: JSON.stringify({ url }) })

export const getBrief = (id: string) => call<Brief>(`/${id}/brief`)
export const updateBrief = (id: string, data: Brief) =>
  call<Brief>(`/${id}/brief`, { method: 'PUT', body: JSON.stringify(data) })

// Material is the one brief field a factory may set (customer too) — narrow path.
export const setSampleMaterial = (id: string, material: string) =>
  call<Brief>(`/${id}/material`, { method: 'PATCH', body: JSON.stringify({ material }) })

// Per-sample price — the factory's quote (factory-only).
export const setSamplePrice = (id: string, price: string) =>
  call<Brief>(`/${id}/price`, { method: 'PATCH', body: JSON.stringify({ price }) })

// Target price — the buyer's target (customer-only).
export const setTargetPrice = (id: string, price: string) =>
  call<Brief>(`/${id}/target-price`, { method: 'PATCH', body: JSON.stringify({ price }) })

// Factory production notes — factory-only (buyer is read-only).
export const setFactoryNotes = (id: string, notes: string) =>
  call<Brief>(`/${id}/factory-notes`, { method: 'PATCH', body: JSON.stringify({ notes }) })

export const listReferences = (id: string) => call<SampleReference[]>(`/${id}/references`)
export const addReference = (id: string, url: string, data?: unknown) =>
  call<SampleReference>(`/${id}/references`, { method: 'POST', body: JSON.stringify({ url, data }) })
export const setMainReference = (id: string, refId: string) =>
  call<unknown>(`/${id}/references/${refId}/set-main`, { method: 'POST' })
export const removeReference = (id: string, refId: string) =>
  call<unknown>(`/${id}/references/${refId}`, { method: 'DELETE' })

export const changePhase = (id: string, phaseKey: string) =>
  call<Sample>(`/${id}/phase`, { method: 'PATCH', body: JSON.stringify({ phase_key: phaseKey }) })
export const factoryConfirm = (id: string, expectedCompletionAt: number) =>
  call<Sample>(`/${id}/factory-confirm`, { method: 'POST', body: JSON.stringify({ expected_completion_at: expectedCompletionAt }) })
export const markReady = (id: string) => call<Sample>(`/${id}/mark-ready`, { method: 'POST' })
export const markShipped = (id: string, payload: { carrier?: string; tracking_number?: string; tracking_url?: string; note?: string }) =>
  call<Sample>(`/${id}/mark-shipped`, { method: 'POST', body: JSON.stringify(payload) })
export const markReceived = (id: string) => call<Sample>(`/${id}/mark-received`, { method: 'POST' })

export type BulkMarkReceivedResult = {
  marked: string[]
  skipped: Array<{ id: string; reason: string }>
}

export const bulkMarkReceived = (ids: string[]) =>
  call<BulkMarkReceivedResult>('/bulk-mark-received', {
    method: 'POST',
    body: JSON.stringify({ ids }),
  })

// ── Catalog adoption (admin-only; received samples) ─────────────────────────

export type AdoptCandidate = {
  master_catalog_id: string
  platform_sku: string
  display_code: string | null
  title: string
  category: string
  matched_via: 'title_similarity'
  confidence: 'medium'
  score: number
  options: Array<{ name: string; values: string[] }>
  sku_count: number
  variant_addable: boolean
}

export type AdoptCandidatesResponse = {
  sample_request_id: string
  title: string | null
  candidates: AdoptCandidate[]
}

export type AdoptPayload = {
  mode: 'add_variation' | 'create_product'
  sku_code: string
  master_catalog_id?: string
  axes?: Record<string, string>
  title?: string
  category?: string
  fulfillment_ecosystem_id?: string
  axis_name?: string
  axis_value?: string
  add_inventory?: boolean
  warehouse_id?: string
  quantity?: number
}

export type AdoptResult = Sample & {
  adopted_product_title: string
  adopted_sku_code: string
  product_created: boolean
  inventory_added: boolean
}

export const getAdoptCandidates = (id: string) =>
  call<AdoptCandidatesResponse>(`/${id}/adopt-candidates`)
export const adoptSample = (id: string, payload: AdoptPayload) =>
  call<AdoptResult>(`/${id}/adopt`, { method: 'POST', body: JSON.stringify(payload) })

export const getActivity = (id: string) => call<SampleActivity[]>(`/${id}/activity`)
export const markRead = (id: string) => call<unknown>(`/${id}/mark-read`, { method: 'POST' })

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

export async function exportSamples(ids: string[], format: 'pdf' | 'xls'): Promise<void> {
  const res = await fetch(`${SAMPLES_BASE}/export`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ ids, format }),
  })
  if (!res.ok) throw new Error(`Export failed (HTTP ${res.status})`)
  triggerDownload(await res.blob(), format === 'pdf' ? 'samples.pdf' : 'samples.xlsx')
}

export async function exportSampleDetail(id: string, code?: string): Promise<void> {
  const res = await fetch(`${SAMPLES_BASE}/${id}/export`)
  if (!res.ok) throw new Error(`Export failed (HTTP ${res.status})`)
  triggerDownload(await res.blob(), `${code || 'sample'}.pdf`)
}

// Print = print the server-generated production sheet PDF, NOT window.print()
// (which would print the raw React page via Chrome's page-print). Load the PDF
// into a hidden same-origin iframe and trigger its print dialog directly.
export async function printSampleDetail(id: string): Promise<void> {
  const res = await fetch(`${SAMPLES_BASE}/${id}/export`)
  if (!res.ok) throw new Error(`Print failed (HTTP ${res.status})`)
  const url = URL.createObjectURL(await res.blob())
  // Offscreen but REAL-sized iframe — a 0×0 iframe often won't render/print a
  // blob PDF in Chrome. Print on load (small delay lets the PDF viewer mount);
  // fall back to opening the sheet in a tab if the iframe print is blocked.
  const iframe = document.createElement('iframe')
  iframe.setAttribute('aria-hidden', 'true')
  iframe.style.position = 'fixed'
  iframe.style.left = '-10000px'
  iframe.style.top = '0'
  iframe.style.width = '794px'
  iframe.style.height = '1123px'
  iframe.style.border = '0'
  iframe.onload = () => {
    setTimeout(() => {
      try {
        iframe.contentWindow?.focus()
        iframe.contentWindow?.print()
      } catch {
        window.open(url, '_blank')
      }
    }, 350)
  }
  iframe.src = url
  document.body.appendChild(iframe)
  setTimeout(() => {
    iframe.remove()
    URL.revokeObjectURL(url)
  }, 120_000)
}

export const createShareLink = (id: string) =>
  call<{ token: string; expires_at: number }>(`/${id}/share`, { method: 'POST' })

// Absolute, public, login-free URL to a sample's production-sheet PDF.
export function sampleShareUrl(token: string): string {
  return `${SAMPLES_BASE}/share/${token}`
}

export const listComments = (id: string) => call<SampleComment[]>(`/${id}/comments`)
export const postComment = (id: string, body: string) =>
  call<SampleComment>(`/${id}/comments`, { method: 'POST', body: JSON.stringify({ body }) })
export const editComment = (id: string, cid: string, body: string) =>
  call<SampleComment>(`/${id}/comments/${cid}`, { method: 'PATCH', body: JSON.stringify({ body }) })
export const deleteComment = (id: string, cid: string) =>
  call<unknown>(`/${id}/comments/${cid}`, { method: 'DELETE' })

// ── Bulk adoption (admin-only worksheet: the whole received shelf at once) ───

type BulkAdoptValue = { value: string; sku_code: string; selected: boolean }

export type BulkAdoptRow = {
  sample_request_id: string
  request_number: number
  title: string
  producer_org_id: string | null
  producer_name: string | null
  supplier_id: string | null
  quote_raw: string | null
  cost_usd: string | null
  material: string | null
  size: string | null
  colors: string[]
  source_url: string | null
  market_seller: string | null
  market_price: string | null
  mode: 'add_variation' | 'create_product'
  candidates: Array<AdoptCandidate & { axis_names: string[] }>
  target_master_catalog_id: string | null
  family_code: string
  category: string
  axis_name: string
  values: BulkAdoptValue[]
  warnings: string[]
}

export type BulkAdoptPlan = {
  rows: BulkAdoptRow[]
  category_default: string
  selection_applied: boolean
}

export type BulkAdoptResult = {
  adopted: number
  failed: number
  results: Array<{
    sample_request_id: string
    status: 'adopted' | 'failed' | 'skipped'
    master_catalog_id?: string
    product_created?: boolean
    sku_codes?: string[]
    reason?: string
    message?: string
  }>
}

export const getBulkAdoptPlan = (ids?: string) =>
  call<BulkAdoptPlan>(`/bulk-adopt/plan${ids ? `?ids=${encodeURIComponent(ids)}` : ''}`)

export const bulkAdopt = (payload: {
  fulfillment_ecosystem_id: string
  rows: Array<{
    sample_request_id: string
    mode: 'add_variation' | 'create_product'
    axis_name: string
    values: Array<{ value: string; sku_code: string }>
    master_catalog_id?: string | null
    family_code?: string | null
    title?: string | null
    category?: string | null
    supplier_id?: string | null
    material?: string | null
    source_url?: string | null
    cost_usd?: string | null
  }>
}) => call<BulkAdoptResult>('/bulk-adopt', { method: 'POST', body: JSON.stringify(payload) })
